import React from 'react';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/system';
import { useTheme } from '../../context/ThemeContext';

export type LoadingSkeletonVariant = 'card' | 'table' | 'list';

export interface LoadingSkeletonProps {
  variant?: LoadingSkeletonVariant;
  count?: number;
}

const shimmerAnimation = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonBlock: React.FC<{
  base: string;
  shimmer: string;
  width?: string;
  height: number;
  borderRadius?: string;
}> = ({ base, shimmer, width = '100%', height, borderRadius = '6px' }) => (
  <Box
    sx={{
      width,
      height,
      borderRadius,
      background: `linear-gradient(90deg, ${base} 25%, ${shimmer} 50%, ${base} 75%)`,
      backgroundSize: '200% 100%',
      animation: `${shimmerAnimation} 1.5s ease-in-out infinite`,
    }}
  />
);

const CardSkeleton: React.FC<{ base: string; shimmer: string }> = ({ base, shimmer }) => (
  <Box
    data-testid="loading-skeleton-card"
    sx={{
      p: 2,
      borderRadius: '8px',
      border: `1px solid ${base}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5,
    }}
  >
    <SkeletonBlock base={base} shimmer={shimmer} height={20} width="60%" />
    <SkeletonBlock base={base} shimmer={shimmer} height={14} width="100%" />
    <SkeletonBlock base={base} shimmer={shimmer} height={14} width="80%" />
  </Box>
);

const TableSkeleton: React.FC<{ base: string; shimmer: string }> = ({ base, shimmer }) => (
  <Box
    data-testid="loading-skeleton-table"
    sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 1.5 }}
  >
    <SkeletonBlock base={base} shimmer={shimmer} height={14} width="25%" />
    <SkeletonBlock base={base} shimmer={shimmer} height={14} width="35%" />
    <SkeletonBlock base={base} shimmer={shimmer} height={14} width="20%" />
    <SkeletonBlock base={base} shimmer={shimmer} height={14} width="20%" />
  </Box>
);

const ListSkeleton: React.FC<{ base: string; shimmer: string }> = ({ base, shimmer }) => (
  <Box
    data-testid="loading-skeleton-list"
    sx={{ display: 'flex', gap: 2, alignItems: 'center', py: 1 }}
  >
    <SkeletonBlock base={base} shimmer={shimmer} height={40} width="40px" borderRadius="50%" />
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <SkeletonBlock base={base} shimmer={shimmer} height={14} width="50%" />
      <SkeletonBlock base={base} shimmer={shimmer} height={12} width="30%" />
    </Box>
  </Box>
);

const variantMap: Record<LoadingSkeletonVariant, React.FC<{ base: string; shimmer: string }>> = {
  card: CardSkeleton,
  table: TableSkeleton,
  list: ListSkeleton,
};

const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  variant = 'card',
  count = 3,
}) => {
  const { theme } = useTheme();
  const base = theme.components?.skeleton?.base ?? '#E5E7EB';
  const shimmer = theme.components?.skeleton?.shimmer ?? '#F3F4F6';
  const Component = variantMap[variant];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {Array.from({ length: count }, (_, i) => (
        <Component key={i} base={base} shimmer={shimmer} />
      ))}
    </Box>
  );
};

export default LoadingSkeleton;
